import * as Blockly from "blockly";
import { pythonGenerator, Order } from "blockly/python";

// Block Navigation ("tank drill") blocks: the generated Python calls the server-side grid runtime.
export const NAV_COLOUR = "#c2703a";
export const NAV_SENSE_COLOUR = "#b4536b";

export const NAV_CATEGORIES = [
  { id: "move", name: "Drill", blocks: [
    { type: "nav_forward", label: "move forward" },
    { type: "nav_backward", label: "move backward" },
    { type: "nav_turn_left", label: "turn left" },
    { type: "nav_turn_right", label: "turn right" },
  ] },
  { id: "loops", name: "Loops", blocks: [
    { type: "controls_repeat_ext", label: "repeat N times" },
    { type: "nav_repeat_until_treasure", label: "repeat until treasure" },
  ] },
  { id: "logic", name: "Logic", blocks: [
    { type: "controls_if", label: "if / if-else" },
    { type: "nav_sensor", label: "sensors (obstacle / danger)" },
    { type: "logic_operation", label: "and / or" },
    { type: "logic_negate", label: "not" },
  ] },
  { id: "maths", name: "Maths", blocks: [
    { type: "math_number", label: "number" },
    { type: "math_arithmetic", label: "+ − × ÷" },
    { type: "logic_compare", label: "compare (= ≠ < >)" },
  ] },
  { id: "variables", name: "Variables", custom: "CD_VARIABLE", blocks: [
    { type: "variables_set", label: "set variable to" },
    { type: "math_change", label: "change variable by" },
    { type: "variables_get", label: "variable value" },
  ] },
  { id: "functions", name: "Functions", custom: "CD_PROCEDURE", blocks: [
    { type: "procedures_defnoreturn", label: "define function" },
    { type: "procedures_callnoreturn", label: "call function" },
  ] },
];

// Families added June 2026 - existing questions keep them hidden until an admin ticks them on
export const NEW_NAV_CATEGORIES = ["maths", "variables", "functions"];

const DOUBLE_COUNT = new Set(["controls_repeat_ext", "nav_repeat_until_treasure", "controls_whileUntil", "controls_if", "procedures_defnoreturn", "procedures_defreturn"]);
const HATS = new Set(["nav_start", "program_start"]);

export const SENSOR_OPTIONS = [
  ["obstacle ahead", "obstacle_ahead"],
  ["obstacle on the left", "obstacle_left"],
  ["obstacle on the right", "obstacle_right"],
  ["danger ahead", "danger_ahead"],
];

let defined = false;
export const defineNavBlocks = () => {
  if (defined) return;
  defined = true;
  Blockly.defineBlocksWithJsonArray([
    { type: "nav_start", message0: "when the drill starts %1 %2", args0: [{ type: "input_dummy" }, { type: "input_statement", name: "BODY" }],
      colour: NAV_COLOUR, tooltip: "Your drill program starts here.", hat: "cap" },
    { type: "nav_forward", message0: "move forward", previousStatement: null, nextStatement: null, colour: NAV_COLOUR, tooltip: "Drive one square forward (rocks stop the drill)" },
    { type: "nav_backward", message0: "move backward", previousStatement: null, nextStatement: null, colour: NAV_COLOUR, tooltip: "Drive one square backward" },
    { type: "nav_turn_left", message0: "turn left ↺", previousStatement: null, nextStatement: null, colour: NAV_COLOUR, tooltip: "Turn 90° to the left, staying on the same square" },
    { type: "nav_turn_right", message0: "turn right ↻", previousStatement: null, nextStatement: null, colour: NAV_COLOUR, tooltip: "Turn 90° to the right, staying on the same square" },
    { type: "nav_repeat_until_treasure", message0: "repeat until treasure reached %1 %2", args0: [{ type: "input_dummy" }, { type: "input_statement", name: "DO" }],
      previousStatement: null, nextStatement: null, colour: "#5ba55b", tooltip: "Keeps repeating the blocks inside until the drill reaches the treasure" },
    { type: "nav_sensor", message0: "%1 ?", args0: [{ type: "field_dropdown", name: "SENSE", options: SENSOR_OPTIONS }], output: "Boolean",
      colour: NAV_SENSE_COLOUR, tooltip: "True when the drill senses this" },
  ]);
  const hat = Blockly.Blocks["nav_start"];
  const baseInit = hat.init;
  hat.init = function () { baseInit.call(this); this.setDeletable(false); this.contextMenu = false; };

  pythonGenerator.forBlock["nav_start"] = (block, generator) => generator.statementToCode(block, "BODY").replace(/^ {2}/gm, "");
  pythonGenerator.forBlock["nav_forward"] = () => "move_forward()\n";
  pythonGenerator.forBlock["nav_backward"] = () => "move_backward()\n";
  pythonGenerator.forBlock["nav_turn_left"] = () => "turn_left()\n";
  pythonGenerator.forBlock["nav_turn_right"] = () => "turn_right()\n";
  pythonGenerator.forBlock["nav_repeat_until_treasure"] = (block, generator) => {
    const body = generator.statementToCode(block, "DO") || generator.PASS;
    return `while not at_treasure():\n${body}`;
  };
  pythonGenerator.forBlock["nav_sensor"] = (block) => [`${block.getFieldValue("SENSE")}()`, Order.FUNCTION_CALL];
};

const TOOLBOX_BLOCKS = {
  nav_forward: { kind: "block", type: "nav_forward" },
  nav_backward: { kind: "block", type: "nav_backward" },
  nav_turn_left: { kind: "block", type: "nav_turn_left" },
  nav_turn_right: { kind: "block", type: "nav_turn_right" },
  controls_repeat_ext: { kind: "block", type: "controls_repeat_ext", inputs: { TIMES: { shadow: { type: "math_number", fields: { NUM: 3 } } } } },
  nav_repeat_until_treasure: { kind: "block", type: "nav_repeat_until_treasure" },
  controls_if: [{ kind: "block", type: "controls_if" }, { kind: "block", type: "controls_if", extraState: { hasElse: true } }],
  nav_sensor: { kind: "block", type: "nav_sensor" },
  logic_operation: { kind: "block", type: "logic_operation" },
  logic_negate: { kind: "block", type: "logic_negate" },
  math_number: { kind: "block", type: "math_number", fields: { NUM: 0 } },
  math_arithmetic: { kind: "block", type: "math_arithmetic", inputs: { A: { shadow: { type: "math_number", fields: { NUM: 1 } } }, B: { shadow: { type: "math_number", fields: { NUM: 1 } } } } },
  logic_compare: { kind: "block", type: "logic_compare", inputs: { A: { shadow: { type: "math_number", fields: { NUM: 1 } } }, B: { shadow: { type: "math_number", fields: { NUM: 1 } } } } },
};
const CATEGORY_COLOUR = { move: NAV_COLOUR, loops: "#5ba55b", logic: "#5b80a5", maths: "#5c81a6", variables: "#a55b80", functions: "#995ba5" };

// Blocks hidden by the question, category exclusions expanded
export const navBannedBlocks = (rules = {}) => {
  const exCats = new Set(rules.excluded_categories || []);
  const banned = new Set(rules.excluded_blocks || []);
  NAV_CATEGORIES.filter((c) => exCats.has(c.id)).forEach((c) => c.blocks.forEach((b) => banned.add(b.type)));
  return banned;
};

// Toolbox minus whatever the question excludes. Variables / Functions are dynamic Blockly
// categories (registered per workspace in BlocksWorkspace) so they can list the student's own names.
export const buildNavToolbox = (rules = {}) => {
  const exCats = new Set(rules.excluded_categories || []);
  const exBlocks = new Set(rules.excluded_blocks || []);
  const contents = NAV_CATEGORIES.filter((c) => !exCats.has(c.id)).map((c) => {
    const allowed = c.blocks.filter((b) => !exBlocks.has(b.type));
    if (!allowed.length) return null;
    if (c.custom) return { kind: "category", name: c.name, colour: CATEGORY_COLOUR[c.id], custom: c.custom };
    return { kind: "category", name: c.name, colour: CATEGORY_COLOUR[c.id], contents: allowed.flatMap((b) => TOOLBOX_BLOCKS[b.type]) };
  }).filter(Boolean);
  return { kind: "categoryToolbox", contents };
};

export const defaultNavWorkspaceState = () => ({
  blocks: { languageVersion: 0, blocks: [{ type: "nav_start", x: 24, y: 24 }] },
});

// Mirrors backend utils/nav.py: loops, ifs and function definitions count as 2, hat not counted, shadows ignored
const walk = (block, out) => {
  if (!block || typeof block !== "object") return;
  out.push(block);
  Object.values(block.inputs || {}).forEach((inp) => inp?.block && walk(inp.block, out));
  if (block.next?.block) walk(block.next.block, out);
};
export const navWorkspaceBlocks = (state) => {
  const out = [];
  (state?.blocks?.blocks || []).forEach((top) => walk(top, out));
  return out.filter((b) => !HATS.has(b.type));
};
export const countNavBlocks = (state) => navWorkspaceBlocks(state).reduce((n, b) => n + (DOUBLE_COUNT.has(b.type) ? 2 : 1), 0);
